import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { isHiddenPropertyKey } from '@/utils/system-properties'

export interface PropertyItemProps {
  label: string
  value: any
  theme?: 'dark' | 'light'
  /** All sibling properties on the node — used to resolve document_id for the source document link */
  allProperties?: Record<string, unknown>
}

export function PropertyItem({ label, value, theme = 'light', allProperties }: PropertyItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()

  // Theme-aware colors
  const colors =
    theme === 'dark'
      ? {
          mutedFg: 'text-neutral-400',
          mutedBg: 'bg-neutral-800/50',
          hoverBg: 'hover:bg-neutral-700/50',
          borderColor: 'border-neutral-700',
          textFg: 'text-neutral-200',
          textFgMuted: 'text-neutral-400',
          buttonBg: 'bg-neutral-800',
          buttonHover: 'hover:bg-neutral-700',
        }
      : {
          mutedFg: 'text-muted-foreground',
          mutedBg: 'bg-muted/40',
          hoverBg: 'hover:bg-muted/50',
          borderColor: 'border-muted',
          textFg: 'text-foreground',
          textFgMuted: 'text-muted-foreground/70',
          buttonBg: 'bg-background',
          buttonHover: 'hover:bg-muted',
        }

  // Handle null/undefined
  if (value === null || value === undefined) {
    return (
      <div className="text-xs">
        <span className={`${colors.mutedFg} capitalize block mb-1 font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        <div className={`${colors.textFgMuted} italic px-2 py-1`}>null</div>
      </div>
    )
  }

  // Special handling for description
  if (label === 'description') {
    return (
      <div className="text-xs space-y-1 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
        <span className="text-blue-400 font-semibold block mb-1.5">Description</span>
        <div className={`${colors.textFg} leading-relaxed whitespace-pre-wrap break-words`}>
          {String(value)}
        </div>
      </div>
    )
  }

  // Special handling for documentation array (Finding entity file paths)
  if (label === 'documentation' && Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className="text-xs">
          <span className={`${colors.mutedFg} capitalize block mb-1 font-medium`}>
            Documentation
          </span>
          <div className={`${colors.textFgMuted} italic px-2 py-1`}>No files attached</div>
        </div>
      )
    }
    return (
      <div className="text-xs space-y-1.5">
        <span className={`${colors.mutedFg} block font-medium`}>Documentation</span>
        <div className="space-y-1">
          {value.map((filePath: string, index: number) => {
            const fileName = String(filePath).split('/').pop() || filePath
            // Only absolute API URLs (/api/v1/files/<s3_key>) are servable;
            // pre-v2 filesystem paths have no backing mount anymore.
            const viewUrl = filePath.startsWith('/api/') ? filePath : null
            if (!viewUrl) {
              return (
                <span key={index} className="truncate text-left block px-3 py-2">
                  {fileName}
                </span>
              )
            }
            return (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className={`h-auto py-2 px-3 text-xs justify-start gap-2 w-full ${colors.buttonBg} ${colors.buttonHover}`}
                onClick={() => window.open(viewUrl, '_blank')}
              >
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate text-left flex-1">{fileName}</span>
              </Button>
            )
          })}
        </div>
      </div>
    )
  }

  // Special handling for source_document_path
  if (label === 'source_document_path') {
    const pathStr = String(value)
    const fileName = pathStr.split('/').pop() || pathStr
    const documentId = allProperties?.document_id

    const handleOpen = () => {
      if (documentId && caseId) {
        navigate(`/cases/${caseId}/documents?doc=${encodeURIComponent(String(documentId))}`)
      } else {
        window.open(pathStr, '_blank')
      }
    }

    return (
      <div className="text-xs space-y-1.5">
        <span className={`${colors.mutedFg} block font-medium`}>Source Document</span>
        <Button
          variant="outline"
          size="sm"
          className={`h-auto py-2 px-3 text-xs justify-start gap-2 w-full ${colors.buttonBg} ${colors.buttonHover}`}
          onClick={handleOpen}
        >
          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate text-left flex-1">{fileName}</span>
        </Button>
      </div>
    )
  }

  // Handle boolean
  if (typeof value === 'boolean') {
    return (
      <div className="text-xs">
        <span className={`${colors.mutedFg} capitalize block mb-1 font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        <div
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            value
              ? 'bg-success-surface text-success-foreground border border-success-border'
              : 'bg-neutral-500/20 text-neutral-400 border border-neutral-500/30'
          }`}
        >
          {value ? '✓ True' : '✗ False'}
        </div>
      </div>
    )
  }

  // Handle numbers
  if (typeof value === 'number') {
    return (
      <div className="text-xs">
        <span className={`${colors.mutedFg} capitalize block mb-1 font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        <div className="font-mono px-2 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded inline-block">
          {value.toLocaleString()}
        </div>
      </div>
    )
  }

  const stringValue = String(value)
  const isLong = stringValue.length > 300

  // Check for URL
  const isUrl = stringValue.startsWith('http://') || stringValue.startsWith('https://')

  // Check for path
  const isPath =
    ['path', 'document_path', 'file_path', 'filepath'].includes(label) ||
    (label.toLowerCase().includes('path') && stringValue.includes('/'))

  // Check for email
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stringValue)

  // Check for date
  const isDate = !Number.isNaN(Date.parse(stringValue)) && /^\d{4}-\d{2}-\d{2}/.test(stringValue)

  if (isUrl) {
    return (
      <div className="text-xs space-y-1">
        <span className={`${colors.mutedFg} capitalize block font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        <a
          href={stringValue}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline break-all text-xs flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3 flex-shrink-0" />
          {stringValue}
        </a>
      </div>
    )
  }

  if (isEmail) {
    return (
      <div className="text-xs space-y-1">
        <span className={`${colors.mutedFg} capitalize block font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        <a
          href={`mailto:${stringValue}`}
          className="text-blue-400 hover:text-blue-300 underline break-all"
        >
          {stringValue}
        </a>
      </div>
    )
  }

  if (isDate) {
    const date = new Date(stringValue)
    // A date-only value names a day, not an instant. `new Date('2011-03-09')` is
    // UTC midnight, so appending a clock invented an hour the source never had
    // (and read back in local time it can even land on the previous day). Only
    // values that actually carry a time of day get one.
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(stringValue.trim())
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(dateOnly ? { timeZone: 'UTC' as const } : {}),
    })
    const formattedTime = dateOnly
      ? null
      : date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })

    return (
      <div className="text-xs">
        <span className={`${colors.mutedFg} capitalize block mb-1 font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        <div className="px-2 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded inline-block">
          {formattedTime ? `${formattedDate} at ${formattedTime}` : formattedDate}
        </div>
      </div>
    )
  }

  if (isPath) {
    const rawPath = stringValue
    // Only absolute API URLs (/api/v1/files/<s3_key>) are servable; pre-v2
    // filesystem paths have no backing mount anymore — show them as text.
    const viewUrl = rawPath.startsWith('/api/') ? rawPath : null
    const fileName = rawPath.split('/').pop() || rawPath

    return (
      <div className="text-xs space-y-1.5">
        <span className={`${colors.mutedFg} capitalize block font-medium`}>
          {label.replace(/_/g, ' ')}
        </span>
        {viewUrl ? (
          <Button
            variant="outline"
            size="sm"
            className={`h-auto py-2 px-3 text-xs justify-start gap-2 w-full ${colors.buttonBg} ${colors.buttonHover}`}
            onClick={() => window.open(viewUrl, '_blank')}
          >
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate text-left flex-1">{fileName}</span>
          </Button>
        ) : (
          <span className="truncate text-left block">{fileName}</span>
        )}
      </div>
    )
  }

  if (isLong) {
    return (
      <div className="text-xs space-y-1 border-l-2 border-primary/20 pl-2">
        <div
          className={`flex items-center gap-1 cursor-pointer ${colors.hoverBg} rounded p-0.5 -ml-1 transition-colors group`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-primary" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-primary" />
          )}
          <span
            className={`${colors.mutedFg} capitalize select-none font-medium group-hover:${colors.textFg}`}
          >
            {label.replace(/_/g, ' ')}
          </span>
          <span
            className={`text-[10px] ${colors.mutedFg} ml-auto ${colors.mutedBg} px-1.5 rounded-full`}
          >
            {stringValue.length} chars
          </span>
        </div>

        {isExpanded ? (
          <div
            className={`font-mono break-words break-all whitespace-pre-wrap ${colors.mutedBg} rounded px-2 py-1 mt-1 animate-in fade-in duration-200 text-xs`}
          >
            {stringValue}
          </div>
        ) : (
          <div
            className={`font-mono break-words break-all whitespace-pre-wrap ${colors.mutedBg} rounded px-2 py-1 ${colors.textFgMuted} cursor-pointer hover:${colors.textFg} transition-colors mt-1 text-xs`}
            onClick={() => setIsExpanded(true)}
            title="Click to expand"
          >
            {stringValue.slice(0, 200)}
            <span className="text-primary font-medium ml-1 text-xs opacity-70">
              ... (click to show more)
            </span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="text-xs space-y-1">
      <span className={`${colors.mutedFg} capitalize block font-medium`}>
        {label.replace(/_/g, ' ')}
      </span>
      <div
        className={`break-words break-all whitespace-pre-wrap ${colors.mutedBg} rounded px-2 py-1`}
      >
        {stringValue}
      </div>
    </div>
  )
}

// Helper function to filter properties - shared logic
export function filterProperties(key: string, isInvestigationEntity: boolean = false): boolean {
  // Always hide these technical/internal properties
  const hiddenProps = [
    'id',
    'labels',
    'name',
    'elementId',
    'case_id',
    'workflow_run_id',
    'node_id',
    'embedding',
    'vector',
    'createdAt',
    'updatedAt',
    'created_at',
    'updated_at',
    'path', // Internal path, use source_document_path instead
  ]

  // System-namespaced properties (`_render_mode`, `_geo_*`, `_temporal_*`,
  // ...) and derived keys such as `name_variants` (ADR-0051) are
  // platform-written, not user data. See `docs/schemas/case-schema-rules.md`
  // § Property namespaces.
  if (isHiddenPropertyKey(key)) return false
  if (hiddenProps.includes(key)) return false
  if (key === 'labels') return false
  if (isInvestigationEntity && ['text', 'isSticky', 'name'].includes(key)) return false

  return true
}

// Helper function to sort properties - shared logic
export function sortProperties([keyA]: [string, any], [keyB]: [string, any]): number {
  // Sort description first, then source_document_path, then alphabetically
  if (keyA === 'description') return -1
  if (keyB === 'description') return 1
  if (keyA === 'source_document_path') return -1
  if (keyB === 'source_document_path') return 1
  return keyA.localeCompare(keyB)
}
